import { format, getISOWeek, getISOWeekYear, parse } from 'date-fns';
import { ensureUtcIso, formatRunWallClock } from './sessionTime';

export type RunGroupMode = 'date' | 'week' | 'month';

export interface RunGroup<T = any> {
  key: string;
  label: string;
  runs: T[];
}

const UNKNOWN_KEY = 'unknown';

// Wall-clock yyyy-MM-dd in the run's own timezone, consistent with what the
// row's datetime column displays.
function wallClockDate(run: { run_ts?: string | null; timezone?: string | null }): string | null {
  const wc = formatRunWallClock(run.run_ts, run.timezone, 'datetime');
  const dateStr = wc ? wc.split(' ')[0] : null;
  return dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? dateStr : null;
}

function bucketFor(dateStr: string, mode: RunGroupMode): { key: string; label: string } {
  const d = parse(dateStr, 'yyyy-MM-dd', new Date());
  if (mode === 'week') {
    const week = getISOWeek(d);
    const year = getISOWeekYear(d);
    return { key: `${year}-W${String(week).padStart(2, '0')}`, label: `Week ${week}, ${year}` };
  }
  if (mode === 'month') {
    return { key: format(d, 'yyyy-MM'), label: format(d, 'MMMM yyyy') };
  }
  return { key: dateStr, label: format(d, 'EEE d MMM yyyy') };
}

// Bucket runs by wall-clock date / ISO week / month. Groups come back newest
// first (unknown dates last), runs newest-first within each group.
export function groupRuns<T extends { run_ts?: string | null; timezone?: string | null }>(
  runs: T[],
  mode: RunGroupMode,
): RunGroup<T>[] {
  const buckets = new Map<string, { label: string; runs: T[] }>();
  for (const run of runs) {
    const dateStr = wallClockDate(run);
    const { key, label } = dateStr
      ? bucketFor(dateStr, mode)
      : { key: UNKNOWN_KEY, label: 'Unknown date' };
    const bucket = buckets.get(key) ?? { label, runs: [] };
    bucket.runs.push(run);
    buckets.set(key, bucket);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => {
      if (a === UNKNOWN_KEY) return 1;
      if (b === UNKNOWN_KEY) return -1;
      return b.localeCompare(a);
    })
    .map(([key, { label, runs: groupRunsList }]) => ({
      key,
      label,
      runs: [...groupRunsList].sort(
        (a, b) => Date.parse(ensureUtcIso(b.run_ts ?? '')) - Date.parse(ensureUtcIso(a.run_ts ?? '')),
      ),
    }));
}
